/**
 * Helpers for deliberately un-awaited promises.
 *
 * The rule these encode: never swallow an error that isn't already handled
 * somewhere else. A bare `.catch(() => {})` is opaque — the reader cannot tell
 * whether the error is genuinely handled downstream or silently dropped with a
 * consequence. Naming the intent makes the decision legible.
 *
 * Reach for these ONLY when the failure has no local consequence:
 *   - HTTP failures are already surfaced globally by the axios interceptor, so a
 *     fire-and-forget request needs no local handling — only suppression of the
 *     duplicate unhandled-rejection.
 *   - Some rejections are expected and non-actionable (e.g. the browser autoplay
 *     policy rejecting `audio.play()`).
 *
 * Do NOT use these to drop an error a downstream consumer depends on. If the
 * value a promise produces is later read as if present, its failure has a
 * consequence — await it and handle the absence instead.
 */

/**
 * A no-op rejection handler for outcomes that are expected and non-actionable.
 * Attach with `.catch(ignoreExpectedRejection)` to document that the rejection
 * is intentional rather than an oversight.
 */
export function ignoreExpectedRejection(): void {
    // The rejection is expected and non-actionable; intentionally do nothing.
}

/**
 * Run a promise as deliberate fire-and-forget. The error is expected to be
 * handled elsewhere (typically the axios interceptor, which already shows the
 * notification); this only suppresses the unhandled-rejection that would
 * otherwise be logged. Returns `void`, so it also satisfies `no-floating-promises`.
 */
export function runInBackground(promise: Promise<unknown>): void {
    promise.catch(ignoreExpectedRejection);
}
